import {
  GetCustomerFiles,
  GetCustomerFileUploadPresignedUrl,
  PostCompleteCustomerFileUpload,
  DeleteCustomerFile,
  GetCustomerFileDownloadPresignedUrl,
} from "@/server-types";
import { get, post, del } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

const PRESIGNED_URL_STALE_MS = 50 * 60 * 1000; // 50 min (URLs expire in 1h)

export const useGetCustomerFileDownloadPresignedUrl = () => {
  return useCustomMutation({
    mutationKey: ["getCustomerFileDownloadPresignedUrl"],
    mutationFn: ({
      id: customerId,
      fileId,
      query,
    }: GetCustomerFileDownloadPresignedUrl["params"] & {
      query?: GetCustomerFileDownloadPresignedUrl["query"];
    }) =>
      get<GetCustomerFileDownloadPresignedUrl>(
        `/storage/file/customer/${customerId}/file/${fileId}/download`,
        query,
      ),
  });
};

export const useGetCustomerFilePresignedUrlQuery = ({
  customerId,
  fileId,
  enabled = true,
}: {
  customerId: number;
  fileId: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["customerFilePresignedUrl", customerId, fileId],
    queryFn: () =>
      get<GetCustomerFileDownloadPresignedUrl>(
        `/storage/file/customer/${customerId}/file/${fileId}/download`,
        { asAttachment: false },
      ),
    enabled: !!customerId && !!fileId && enabled,
    staleTime: PRESIGNED_URL_STALE_MS,
  });
};

export const useDeleteCustomerFile = () => {
  return useCustomMutation({
    mutationKey: ["deleteCustomerFile"],
    mutationFn: ({ id: customerId, fileId }: DeleteCustomerFile["params"]) =>
      del(`/storage/file/customer/${customerId}/file/${fileId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCustomerFiles"],
      });

      queryClient.invalidateQueries({
        queryKey: ["useGetCustomerNotes"],
      });
    },
  });
};

export const useGetCustomerFiles = ({
  customerId,
  query,
}: {
  customerId?: number;
  query: GetCustomerFiles["query"];
}) => {
  return useCustomQuery({
    queryKey: ["getCustomerFiles", customerId, query],
    queryFn: () =>
      get<GetCustomerFiles>(`/storage/file/customer/${customerId!}`, query),
    enabled: !!customerId,
  });
};

export const useGetCustomerFileUploadPresignedUrl = () => {
  return useCustomMutation({
    mutationKey: ["getCustomerFileUploadPresignedUrl"],
    mutationFn: ({
      customerId,
      query,
    }: {
      customerId: number;
      query: GetCustomerFileUploadPresignedUrl["query"];
    }) =>
      get<GetCustomerFileUploadPresignedUrl>(
        `/storage/file/customer/${customerId}/upload`,
        query,
      ),
  });
};

export const useCompleteCustomerFileUpload = () => {
  return useCustomMutation({
    mutationKey: ["completeCustomerFileUpload"],
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number;
      body: PostCompleteCustomerFileUpload["body"];
    }) =>
      post<PostCompleteCustomerFileUpload>(
        `/storage/file/customer/${customerId}/upload/complete`,
        body,
      ),
  });
};
